import "@/lib/url-parse-polyfill";
import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import "@/lib/analytics";
import "@/hooks/use-utm";
import { installCsrfFetchInterceptor, ensureCsrfToken } from "@/lib/csrf";
import { installAuthenticatedFileClickInterceptor } from "@/lib/authenticated-file-clicks";
import { loadUploadCap } from "@/lib/upload-helpers";
import { isStaleChunkError, attemptStaleChunkReload } from "@/lib/stale-chunk";
import {
  getConsent,
  isConsentStale,
  applyConsentToDataLayer,
} from "@/lib/consent-store";

// A deploy re-hashes every lazy-route chunk. A tab still running the old bundle
// throws when it tries to import a chunk name that no longer exists. Vite fires
// `vite:preloadError` on window for exactly this case; recover with a one-time,
// loop-guarded full reload onto the new version instead of letting it bubble to
// the error boundary's maintenance screen. preventDefault stops Vite's default
// re-throw. Task #3081.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  attemptStaleChunkReload();
});

// Belt-and-suspenders for browsers/paths where a failed dynamic import surfaces
// as an unhandled rejection rather than (or before) `vite:preloadError`.
window.addEventListener("unhandledrejection", (event) => {
  if (isStaleChunkError(event.reason)) {
    event.preventDefault();
    attemptStaleChunkReload();
  }
});

installCsrfFetchInterceptor();
installAuthenticatedFileClickInterceptor();
void ensureCsrfToken();
// Warm the upload-cap cache early so the first oversized file pick
// shows its friendly error without a network round-trip (Task #775).
void loadUploadCap();

// Apply stored consent to Consent Mode v2 as early as possible in the
// module bundle. The inline script in index.html already does this
// before React boots; this call is belt-and-suspenders for cases where
// the inline script ran before the dataLayer was fully ready, and
// ensures the React-side store is in sync with GTM's state.
{
  const stored = getConsent();
  if (!isConsentStale(stored) && stored) {
    applyConsentToDataLayer(stored.grants);
  }
}


// Sentry is our PRODUCTION watch only. The dev/workspace build is never
// reported — local errors show live in the browser console, and reporting them
// would dilute the production signal and burn through the event quota.
if (import.meta.env.VITE_SENTRY_DSN && import.meta.env.PROD) {
  // PII scrubber mirrored from server/lib/sentry.ts. Strips credential
  // and PII-shaped keys plus email / bearer-token substrings.
  const SENSITIVE_KEY_RE =
    /password|secret|token|authorization|cookie|api[_-]?key|email|phone|ssn|dob|birth|address|postal|zip/i;
  const EMAIL_RE = /[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/g;
  const BEARER_RE = /(Bearer\s+)[A-Za-z0-9._\-]+/gi;
  const scrubString = (s: string) =>
    s.replace(BEARER_RE, "$1[Filtered]").replace(EMAIL_RE, "[email]");
  const scrub = (value: unknown, depth = 0): unknown => {
    if (value == null || depth > 8) return value;
    if (typeof value === "string") return scrubString(value);
    if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1));
    if (typeof value === "object") {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[k] = SENSITIVE_KEY_RE.test(k) ? "[Filtered]" : scrub(v, depth + 1);
      }
      return out;
    }
    return value;
  };
  const scrubInPlace = (obj: Record<string, unknown> | undefined | null) => {
    if (!obj || typeof obj !== "object") return;
    for (const k of Object.keys(obj)) {
      if (SENSITIVE_KEY_RE.test(k)) {
        obj[k] = "[Filtered]";
        continue;
      }
      const v = obj[k];
      if (typeof v === "string") obj[k] = scrubString(v);
      else if (v && typeof v === "object") obj[k] = scrub(v, 1);
    }
  };

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    // In production, skip the high-frequency per-portal auth-status probes
    // (/api/*/me) that fire on every page load / React Query poll cycle and
    // drop all other navigations to 2 % (was a flat 10 % with no filter).
    // Dev keeps 100 % so local debugging is unaffected.
    tracesSampler(ctx) {
      if (!import.meta.env.PROD) return 1.0;
      const name: string =
        (ctx as { name?: string }).name ??
        (ctx as { attributes?: { "url.path"?: string } }).attributes?.[
          "url.path"
        ] ??
        "";
      if (/\/api\/[^/]+\/me($|\?)/.test(name)) return 0;
      return 0.02;
    },
    replaysSessionSampleRate: 0,
    // 10 % of error sessions get a replay (was 100 %). This stops replays
    // from pinning the quota while errors still report fully via beforeSend.
    replaysOnErrorSampleRate: 0.1,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    beforeSend(event) {
      // Successful post-deploy chunk reloads are expected; only loop-guard
      // failures (reloaded=false) should reach Sentry / the ops digest.
      const kind = event.tags?.kind;
      if (
        kind === "stale_chunk_reload" &&
        event.tags?.reloaded !== "false"
      ) {
        return null;
      }
      if (
        event.message === "stale_chunk_auto_reload" &&
        event.tags?.reloaded !== "false"
      ) {
        return null;
      }
      if (event.message) event.message = scrubString(event.message);
      if (event.request?.data && typeof event.request.data === "object") {
        scrubInPlace(event.request.data as Record<string, unknown>);
      }
      if (event.request?.headers) scrubInPlace(event.request.headers as Record<string, unknown>);
      if (event.user) scrubInPlace(event.user as unknown as Record<string, unknown>);
      if (event.contexts) {
        for (const k of Object.keys(event.contexts)) {
          const ctx = (event.contexts as Record<string, unknown>)[k];
          if (ctx && typeof ctx === "object") scrubInPlace(ctx as Record<string, unknown>);
        }
      }
      if (event.extra) scrubInPlace(event.extra as Record<string, unknown>);
      if (event.tags) scrubInPlace(event.tags as Record<string, unknown>);
      if (Array.isArray(event.breadcrumbs)) {
        for (const bc of event.breadcrumbs) {
          if (bc?.data) scrubInPlace(bc.data as Record<string, unknown>);
          if (typeof bc?.message === "string") bc.message = scrubString(bc.message);
        }
      }
      if (event.exception?.values && Array.isArray(event.exception.values)) {
        for (const ex of event.exception.values) {
          if (typeof ex.value === "string") ex.value = scrubString(ex.value);
          if (ex.mechanism?.data) scrubInPlace(ex.mechanism.data as Record<string, unknown>);
        }
      }
      return event;
    },
  });
}

createRoot(document.getElementById("root")!).render(<App />);

// Note: the static "We'll be right back" fallback baked into index.html is NOT
// removed here. Removing it right after render() would only confirm that
// render() was *called*, not that the app actually committed a usable UI. It is
// instead removed from a mount-confirmed layout effect inside App.tsx (both the
// healthy tree and the global error fallback), which fires after React commits
// and before the browser paints — so a broken boot keeps the message visible
// and a healthy boot never flashes it over real content.
